// nuolino-admin/src/lib/sse.ts
// Wrapper de EventSource (ADR-008 SSE).
// API ergonômica + reconexão com backoff + heartbeat check.
//
// O navegador NÃO suporta custom headers em EventSource, então auth vai
// por cookie httpOnly (nuolino_at). BFF revalida a cada 60s. Este wrapper
// só encapsula o transporte, parse de eventos, reconexão e cleanup.

export interface SseMessage {
  id: string | null;
  event: string;
  data: unknown;
}

export interface SseOptions {
  /** EventSource constructor override (testes). */
  EventSourceImpl?: typeof EventSource;
  /** Backoff inicial em ms. Default 1_000. */
  initialBackoffMs?: number;
  /** Backoff máximo em ms. Default 30_000. */
  maxBackoffMs?: number;
  /** Se recebe mensagem de heartbeat no intervalo, reseta backoff. */
  heartbeatMs?: number;
  /** Called em qualquer estado: connecting | open | closed | error. */
  onState?: (state: SseState) => void;
}

export type SseState = 'connecting' | 'open' | 'closed' | 'error';

export interface SseConnection {
  close(): void;
  readonly state: () => SseState;
}

export function connectSse(url: string, handlers: Partial<Record<string, (msg: SseMessage) => void>>, options: SseOptions = {}): SseConnection {
  const EventSourceImpl = options.EventSourceImpl ?? (globalThis as { EventSource?: typeof EventSource }).EventSource;
  if (!EventSourceImpl) {
    throw new Error('EventSource is not available in this environment');
  }
  const initialBackoff = options.initialBackoffMs ?? 1_000;
  const maxBackoff = options.maxBackoffMs ?? 30_000;
  const heartbeatMs = options.heartbeatMs ?? 60_000;

  let state: SseState = 'connecting';
  let backoff = initialBackoff;
  let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let es: EventSource | null = null;
  let closed = false;
  let lastEventId: string | null = null;

  function setState(next: SseState): void {
    state = next;
    options.onState?.(next);
  }

  function resetHeartbeat(): void {
    if (heartbeatTimer) clearTimeout(heartbeatTimer);
    heartbeatTimer = setTimeout(() => {
      // Sem heartbeat em heartbeatMs: assume conexão zumbi, fecha para reconectar.
      es?.close();
      scheduleReconnect();
    }, heartbeatMs);
  }

  function scheduleReconnect(): void {
    if (closed) return;
    if (reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      open();
    }, backoff);
    backoff = Math.min(backoff * 2, maxBackoff);
  }

  function parseData(raw: string): unknown {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }

  function open(): void {
    if (closed) return;
    setState('connecting');
    const init: { withCredentials?: true; headers?: Record<string, string>; lastEventId?: string } = { withCredentials: true };
    // Last-Event-ID para replay (server pode usar para resumability).
    if (lastEventId) init.lastEventId = lastEventId;
    const Source = EventSourceImpl as typeof EventSource;
    es = new Source(url, init as unknown as EventSourceInit);

    es.onopen = () => {
      setState('open');
      backoff = initialBackoff;
      resetHeartbeat();
    };
    es.onerror = () => {
      setState('error');
      if (heartbeatTimer) clearTimeout(heartbeatTimer);
      es?.close();
      scheduleReconnect();
    };
    es.onmessage = (e: MessageEvent) => {
      lastEventId = e.lastEventId || lastEventId;
      resetHeartbeat();
      handlers.message?.({ id: lastEventId, event: 'message', data: parseData(e.data) });
    };
    for (const [name, handler] of Object.entries(handlers)) {
      if (name === 'message' || !handler || !es) continue;
      es.addEventListener(name, (e) => {
        const me = e as MessageEvent;
        lastEventId = me.lastEventId || lastEventId;
        resetHeartbeat();
        handler({ id: lastEventId, event: name, data: parseData(me.data) });
      });
    }
  }

  open();

  return {
    close(): void {
      closed = true;
      if (heartbeatTimer) clearTimeout(heartbeatTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      es?.close();
      setState('closed');
    },
    state: () => state,
  };
}
